import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import SubdomainRedirect from './components/SubdomainRedirect';

// Pages
import CustomerLogin from './pages/CustomerLogin';
import HandlerLogin from './pages/HandlerLogin';
import AdminLogin from './pages/AdminLogin';
import AdminAuthCallback from './pages/AdminAuthCallback';
import AdminForgotPassword from './pages/AdminForgotPassword';
import AdminPasswordResetCallback from './pages/AdminPasswordResetCallback';
import CustomerSelect from './pages/CustomerSelect';
import CustomerDashboardLayout from './pages/CustomerDashboardLayout';
import CustomerChat from './pages/CustomerChat';
import CustomerDashboard from './pages/CustomerDashboard';
import CustomerGroupList from './pages/CustomerGroupList';
import HandlerDashboard from './pages/HandlerDashboard';
import HandlerSettings from './pages/HandlerSettings';
import HandlerChat from './pages/HandlerChat';
import HandlerProfile from './pages/HandlerProfile';
import AdminDashboard from './pages/AdminDashboard';
import AdminSettings from './pages/AdminSettings';
import AdminActivityDashboard from './pages/AdminActivityDashboard';
import InactiveClientsTracker from './pages/InactiveClientsTracker';
import ClientsByIP from './pages/ClientsByIP';
import EditClient from './pages/EditClient';
import CreateClient from './pages/CreateClient';
import AdminChat from './pages/AdminChat';
import ClientList from './pages/ClientList';
import BroadcastPage from './pages/BroadcastPage';
import GroupListPage from './pages/GroupListPage';
import CreateGroupPage from './pages/CreateGroupPage';
import GroupChatPage from './pages/GroupChatPage';
import GroupDashboard from './pages/GroupDashboard';
import AddGroupMembers from './pages/AddGroupMembers';
import InquiryChat from './pages/InquiryChat';
import SupportChat from './pages/SupportChat';
import AdminChangePassword from './pages/AdminChangePassword';
import AdminChangeMobile from './pages/AdminChangeMobile';
import AdminChangeEmail from './pages/AdminChangeEmail';
import SupportAnalytics from './pages/SupportAnalytics';
import MBConfig from './pages/MBConfig';
import ServiceTimelines from './components/settings/ServiceTimelines';

import './App.css';

// Protected Route Component - Role-aware redirect to prevent login page flash
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-semibold text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!user) {
    // Role-aware redirect based on URL path to prevent wrong login page flash
    const pathname = window.location.pathname;
    if (pathname.startsWith('/admin')) {
      return <Navigate to="/admin-login" replace />;
    } else if (pathname.startsWith('/handler')) {
      return <Navigate to="/handler-login" replace />;
    }
    return <Navigate to="/customer-login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to appropriate login based on current user's role
    const userRole = user.role;
    if (userRole === 'super_admin') {
      return <Navigate to="/admin-login" replace />;
    } else if (['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler'].includes(userRole)) {
      return <Navigate to="/handler-login" replace />;
    }
    return <Navigate to="/customer-login" replace />;
  }

  return children;
};

// Role-based redirect for PWA start_url (/?role=admin, /?role=handler, /?role=customer)
const RoleBasedRedirect = () => {
  const { user } = useAuth();
  if (user && user.role === 'customer' && !user.is_guest) return <Navigate to="/customer-select" replace />;
  if (user && user.role === 'super_admin') return <Navigate to="/admin-dashboard" replace />;
  if (user && user.role && user.role !== 'customer') return <Navigate to="/handler-dashboard" replace />;
  
  const urlParams = new URLSearchParams(window.location.search);
  const role = urlParams.get('role');
  
  if (role === 'admin') return <Navigate to="/admin-login" replace />;
  if (role === 'handler') return <Navigate to="/handler-login" replace />;
  if (role === 'customer') return <Navigate to="/customer-login" replace />;

  // Persistent role memory for PWA launch and 12-hour auto-logout
  const lastRole = localStorage.getItem('dws_last_role');
  if (lastRole === 'admin') return <Navigate to="/admin-login" replace />;
  if (lastRole === 'handler') return <Navigate to="/handler-login" replace />;

  return <Navigate to="/customer-login" replace />;
};

// Direct Chat Link Components (No login required initially)
const DirectChatLink = ({ chatType }) => {
  const { user } = useAuth();
  
  if (!user) {
    // Redirect to customer login with chat type
    return <Navigate to={`/customer-login?redirect=${chatType}`} />;
  }
  
  return <Navigate to={`/customer-chat?type=${chatType}`} />;
};

function AppRoutes() {
  return (
    <>
      <SubdomainRedirect />
      <Routes>
        {/* Login Pages */}
        <Route path="/customer-login" element={<CustomerLogin />} />
        <Route path="/handler-login" element={<HandlerLogin />} />
        <Route path="/admin-login" element={<AdminLogin />} />
        <Route path="/admin-auth-callback" element={<AdminAuthCallback />} />
        <Route path="/admin-forgot-password" element={<AdminForgotPassword />} />
        <Route path="/admin-password-reset-callback" element={<AdminPasswordResetCallback />} />

        {/* Short URL aliases for direct access */}
        <Route path="/admin" element={<Navigate to="/admin-login" replace />} />
        <Route path="/handler" element={<Navigate to="/handler-login" replace />} />
        <Route path="/customer" element={<Navigate to="/customer-login" replace />} />
        <Route path="/login" element={<Navigate to="/customer-login" replace />} />

      {/* Direct Chat Links */}
      <Route path="/deposit" element={<DirectChatLink chatType="deposit" />} />
      <Route path="/withdraw" element={<DirectChatLink chatType="withdrawal" />} />
      <Route path="/support" element={<DirectChatLink chatType="support" />} />

      {/* Inquiry Chat (No login required) */}
      <Route path="/inquiry-chat" element={<InquiryChat />} />
      <Route path="/support-chat" element={<SupportChat />} />

      {/* Admin Profile Update Routes */}
      <Route
        path="/admin-change-password"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminChangePassword />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-change-mobile"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminChangeMobile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-change-email"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminChangeEmail />
          </ProtectedRoute>
        }
      />

      {/* Customer Routes */}
      <Route
        path="/customer-select"
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerDashboardLayout />
          </ProtectedRoute>
        }
      />
      {/* CustomerChat - No auth protection, handles inquiry guests internally */}
      <Route
        path="/customer-chat"
        element={<CustomerChat />}
      />
      <Route
        path="/customer-dashboard"
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerDashboard />
          </ProtectedRoute>
        }
      />

      {/* Customer Groups List */}
      <Route
        path="/customer/groups"
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <CustomerGroupList />
          </ProtectedRoute>
        }
      />

      {/* Customer Group Chat */}
      <Route
        path="/customer/group/:groupId"
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <GroupChatPage />
          </ProtectedRoute>
        }
      />


      {/* Handler Routes */}
      <Route
        path="/handler-dashboard"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'calling_handler']}>
            <HandlerDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/handler-settings"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'calling_handler', 'super_admin']}>
            <HandlerSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/handler-setting"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'calling_handler', 'super_admin']}>
            <HandlerSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/handler-chat/:roomId"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'calling_handler']}>
            <HandlerChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/handler-profile/:customerId"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'calling_handler', 'super_admin']}>
            <HandlerProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-list"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'calling_handler', 'super_admin']}>
            <ClientList />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin-dashboard"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-settings"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminSettings />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/support-analytics"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SupportAnalytics />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/mb-config"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <MBConfig />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/clients-by-ip"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <ClientsByIP />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/edit-client"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <EditClient />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/create-client"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <CreateClient />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/service-timelines"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'admin']}>
            <ServiceTimelines onBack={() => window.history.back()} />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/handler/create-client"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler']}>
            <CreateClient />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-activity"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminActivityDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/inactive-clients"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'deposit_handler', 'calling_handler']}>
            <InactiveClientsTracker />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-chat/:roomId"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-chat"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <AdminChat />
          </ProtectedRoute>
        }
      />

      {/* Broadcast Routes (Handler + Admin) */}
      <Route
        path="/broadcast"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'super_admin']}>
            <BroadcastPage />
          </ProtectedRoute>
        }
      />

      {/* Group Routes (Handler + Admin) */}
      <Route
        path="/groups"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'super_admin']}>
            <GroupListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups/create"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'super_admin']}>
            <CreateGroupPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups/:groupId"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'super_admin']}>
            <GroupChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups/:groupId/dashboard"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'super_admin']}>
            <GroupDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups/:groupId/add-members"
        element={
          <ProtectedRoute allowedRoles={['deposit_handler', 'withdrawal_handler', 'support_handler', 'inquiry_handler', 'super_admin']}>
            <AddGroupMembers />
          </ProtectedRoute>
        }
      />

      {/* Default Route - Role-aware redirect for PWA */}
      <Route path="/" element={<RoleBasedRedirect />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-center" richColors />
        <PWAInstallPrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
